import { IsoCountryCode2, Language } from '@rbilabs/intl';
import { CognitoUserSession } from 'amazon-cognito-identity-js';

import { userHasSignedIn as userHasSignedInOnCognito } from 'remote/auth';
import { initCognitoSession } from 'utils/cognito';
import { RBIEnv, env } from 'utils/environment';
import { getAppCountry, getAppLanguage } from 'utils/i18n';
import {
  LANGUAGE_SEARCH_PARAM,
  shouldUseLanguageCodeInUrls as getShouldUseLanguageCodeInUrls,
  shouldRedirectToLanguagePath,
} from 'utils/i18n/language-inference';
import { LaunchDarklyFlag, LaunchDarklyFlagsObject, initLaunchDarkly } from 'utils/launchdarkly';
import {
  backgroundUpdate,
  enable as enableLiveUpdates,
  forceUpdate,
  getCurrentVersion,
} from 'utils/live-updates';
import * as location from 'utils/location';
import { logger } from 'utils/logger';
import { createRedirectHandler } from 'utils/routing';

declare global {
  interface Window {
    /**
     * window.Cypress exists when our app is running within Cypress
     * Places we currently check for this:
     * - frontend/src/state/graphql/links/index.ts - avoid BatchHttpLink
     *
     * https://docs.cypress.io/faq/questions/using-cypress-faq.html#Is-there-any-way-to-detect-if-my-app-is-running-under-Cypress
     */
    Cypress?: any;
    // When run with cypress for cypress-v2 package,
    // we instantiate the LD client with predefined flags
    _initial_cypress_feature_flags?: object;
    // Skip the interval for checking for unavailable items.
    // Avoids race condition in cart with recorded tests
    _skipUnavailableItemsIntervalCheck?: boolean;
  }
}

export interface RenderAppOptions {
  appLanguage: Language;
  appCountry: IsoCountryCode2;
  flags: LaunchDarklyFlagsObject;
  userHasSignedIn: boolean;
  userSession: CognitoUserSession | null;
  shouldUseLanguageCodeInUrls: boolean;
}

export type RenderApp = (options: RenderAppOptions) => void;

export const bootstrap = async (renderApp: RenderApp): Promise<void> => {
  const ENV = env();

  const userSessionPromise = initCognitoSession();
  const flags = await initLaunchDarkly();
  const versionData = await getCurrentVersion();

  logger.setContext({
    appflowBuildId: versionData.appflowBuildId,
    appVersionCode: versionData.build,
    appVersionName: versionData.version,
  });

  // ** LANGUAGE INFERENCE **
  // Language inference has to be done as soon as possible.
  // This is currently kept in the function body due to the dependency on the LD
  // `disableLocalization` flag.  Should we no longer depend on it, this code
  // block can be moved to the module level.
  const isLocalizationDisabled = Boolean(flags[LaunchDarklyFlag.DISABLE_LOCALIZATION]);
  const appCountry = getAppCountry();
  const appLanguage = getAppLanguage(isLocalizationDisabled);
  const shouldUseLanguageCodeInUrls = getShouldUseLanguageCodeInUrls(isLocalizationDisabled);

  // Handle legacy URLs
  if (shouldUseLanguageCodeInUrls) {
    const pathname = location.get('pathname');
    const searchParams = location.getSearchParams();

    if (searchParams.has(LANGUAGE_SEARCH_PARAM)) {
      searchParams.delete(LANGUAGE_SEARCH_PARAM);
    }

    if (shouldRedirectToLanguagePath(pathname)) {
      createRedirectHandler(appLanguage)(
        `${pathname}${searchParams.size > 0 ? `?${searchParams}` : ''}`
      );

      return;
    }
  }

  // Only handle live updates when enabled
  if (flags[LaunchDarklyFlag.ENABLE_APP_FLOW_UPDATE]) {
    const shouldUseRbiLiveUpdates = Boolean(
      flags[LaunchDarklyFlag.ROLLOUT_ENABLE_RBI_LIVE_UPDATES]
    );

    await enableLiveUpdates(shouldUseRbiLiveUpdates);

    // For blocking update immediately sync and reload the app
    // Since this happens when the splash is still visible it won't blink
    if (flags[LaunchDarklyFlag.ENABLE_FORCE_BLOCKING_UPDATE]) {
      await forceUpdate(shouldUseRbiLiveUpdates);
      // When the forced update is not enabled just sync in background
      // And reload on next foreground
    } else {
      backgroundUpdate(shouldUseRbiLiveUpdates);
    }
  }

  const userSession = await userSessionPromise;

  // this has to be after init cognito session.
  const userHasSignedIn = await userHasSignedInOnCognito();

  if (ENV === RBIEnv.TEST) {
    const defaultFlags = {};
    renderApp({
      appLanguage,
      appCountry,
      flags: defaultFlags,
      userHasSignedIn,
      userSession,
      shouldUseLanguageCodeInUrls,
    });
    return;
  }

  renderApp({
    appLanguage,
    appCountry,
    flags,
    userHasSignedIn,
    userSession,
    shouldUseLanguageCodeInUrls,
  });
};
